import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PenLine } from "lucide-react";
import { sr } from "../../lib/sr";

const NAVY = "#0F1A3C";
const DEFAULT_GOLD = "#C8A248";
const API = process.env.REACT_APP_BACKEND_URL || "";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

function Card({ children, style, ...rest }) {
  return <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: 22, ...style }} {...rest}>{children}</div>;
}

export default function PublicProposal() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [accepted, setAccepted] = useState(false);

  const load = () => sr.publicProposal(token).then((r) => setCtx(r.proposal)).catch((e) => setErr(e?.response?.data?.detail || "This proposal link is invalid or has been revoked."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (err) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F8FAFC", padding: 20 }}>
      <Card style={{ maxWidth: 440, textAlign: "center" }}><div style={{ fontSize: 15, color: "#334155" }} data-testid="public-proposal-error">{err}</div></Card>
    </div>
  );
  if (!ctx) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#F8FAFC", color: "#64748B" }}>Loading…</div>;

  const pr = ctx.pricing || {};
  const comp = ctx.competitor || {};
  const accent = ctx.theme?.accent_color || DEFAULT_GOLD;
  const logoUrl = ctx.theme?.logo_url ? `${API}${ctx.theme.logo_url}` : null;
  const coverUrl = ctx.theme?.cover_url ? `${API}${ctx.theme.cover_url}` : null;
  const acc = ctx.acceptance || {};
  const showAccept = acc.acceptable && !accepted;
  const alreadyDone = accepted || acc.already_accepted;

  const H2 = ({ children }) => <h2 style={{ fontSize: 13, letterSpacing: 1.5, textTransform: "uppercase", color: accent, fontWeight: 800, margin: "0 0 10px" }}>{children}</h2>;

  return (
    <div data-testid="public-proposal-page" style={{ minHeight: "100vh", background: "#F3F4F6", paddingBottom: 48 }}>
      {coverUrl ? <div data-testid="public-cover" style={{ height: 180, backgroundImage: `url(${coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }} /> : null}
      <div style={{ background: NAVY, color: "#fff", padding: "40px 22px" }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          {logoUrl ? <img src={logoUrl} alt="logo" data-testid="public-logo" style={{ height: 46, maxWidth: 200, objectFit: "contain", marginBottom: 14 }} /> : null}
          <div style={{ fontSize: 12, letterSpacing: 3, color: accent, fontWeight: 800, textTransform: "uppercase" }}>{ctx.brand} · {ctx.endorsement}</div>
          <h1 style={{ fontSize: 34, margin: "12px 0 4px" }}>Advertising Proposal</h1>
          <div style={{ fontSize: 22, color: accent, fontWeight: 700 }}>{ctx.prospect?.name}</div>
          <div style={{ fontSize: 13, color: "#CBD5E1", marginTop: 8 }}>
            {[ctx.prospect?.industry_label, ctx.prospect?.website, ctx.prospect?.geography].filter(Boolean).join(" · ")}
          </div>
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>Issued {ctx.issued_at} · valid until {ctx.valid_until}</div>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "22px", display: "grid", gap: 16 }}>
        {(ctx.objective || ctx.promote) && (
          <Card>
            <H2>The brief</H2>
            {ctx.objective && <p style={{ margin: "0 0 8px", fontSize: 15, color: "#1E293B" }}><strong>Objective:</strong> {ctx.objective}</p>}
            {ctx.promote && <p style={{ margin: "0 0 8px", fontSize: 15, color: "#1E293B" }}><strong>Promoting:</strong> {ctx.promote}</p>}
            <div style={{ fontSize: 13, color: "#64748B" }}>
              {ctx.geography ? `Geography: ${ctx.geography}` : ""}{ctx.geography && ctx.timing ? " · " : ""}{ctx.timing ? `Timing: ${ctx.timing}` : ""}
            </div>
          </Card>
        )}

        {(ctx.targeting_groups || []).length > 0 && (
          <Card>
            <H2>Targeting strategy</H2>
            {ctx.targeting_groups.map((g) => (
              <div key={g.label} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 4 }}>{g.label}</div>
                {g.items.map((it, i) => (
                  <div key={i} style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>
                    <strong>{it.name}</strong>{it.why ? ` — ${it.why}` : ""}
                    {it.channel ? <span style={{ color: "#64748B" }}> · {it.channel}</span> : ""}
                  </div>
                ))}
              </div>
            ))}
            {(comp.brands?.length > 0 || comp.locations?.length > 0) && (
              <div style={{ marginTop: 6, fontSize: 13, color: "#64748B" }}>
                {comp.brands?.length > 0 && <div><strong>Competitor brands:</strong> {comp.brands.join(", ")}</div>}
                {comp.locations?.length > 0 && <div><strong>Locations:</strong> {comp.locations.slice(0, 8).join(", ")}</div>}
              </div>
            )}
          </Card>
        )}

        {(ctx.media_channels || []).length > 0 && (
          <Card>
            <H2>Media plan &amp; investment</H2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: NAVY, color: "#fff" }}>
                  <th style={{ textAlign: "left", padding: "8px 10px" }}>Channel</th>
                  <th style={{ textAlign: "left", padding: "8px 10px" }}>Role</th>
                  <th style={{ textAlign: "right", padding: "8px 10px" }}>Mix</th>
                  <th style={{ textAlign: "right", padding: "8px 10px" }}>Media</th>
                </tr>
              </thead>
              <tbody>
                {ctx.media_channels.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 ? "#F8FAFC" : "#fff" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.label}</td>
                    <td style={{ padding: "8px 10px", color: "#64748B" }}>{c.role}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>{c.allocation_pct || 0}%</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>{money(c.media_spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, background: "#F8FAFC", borderRadius: 10, padding: 14, maxWidth: 360, marginLeft: "auto" }}>
              <Row l="Media (all-in)" v={money(pr.media_only)} />
              <Row l="Creative / content" v={money(pr.content_total)} />
              <Row l={`Management fee (${pr.management_fee_pct || 15}%)`} v={money(pr.management_fee)} />
              {pr.platform_fee ? <Row l="Platform fee" v={money(pr.platform_fee)} /> : null}
              <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
                <Row l={<strong style={{ color: NAVY }}>Total proposed investment</strong>} v={<strong style={{ color: NAVY }}>{money(pr.total)}</strong>} />
              </div>
            </div>
            {(ctx.addons || []).length > 0 && (
              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 10 }}>
                Optional add-ons (require account setup): {ctx.addons.map((a) => a.label).join(", ")}
              </div>
            )}
          </Card>
        )}

        <Card>
          <H2>Your strategist</H2>
          <div style={{ fontSize: 14, color: "#1E293B" }}>{ctx.contact?.name}{ctx.contact?.email ? ` · ${ctx.contact.email}` : ""}</div>
          <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 12, lineHeight: 1.6 }}>{ctx.disclaimer}</div>
        </Card>

        {alreadyDone ? (
          <Card style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", textAlign: "center" }} data-testid="public-accepted">
            <CheckCircle2 size={30} style={{ color: "#059669" }} />
            <h3 style={{ margin: "8px 0 4px", color: "#065F46" }}>Proposal accepted — thank you!</h3>
            <p style={{ fontSize: 13.5, color: "#047857", margin: 0 }}>We've started your onboarding. {accepted ? "Check your inbox for an email to set your password." : ""} Your strategist will be in touch shortly. No billing has been charged.</p>
          </Card>
        ) : showAccept ? (
          <AcceptCard token={token} accent={accent} company={ctx.prospect?.name} onDone={() => { setAccepted(true); toast.success("Proposal accepted"); }} />
        ) : null}
      </div>
    </div>
  );
}

function AcceptCard({ token, accent, company, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const field = { width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, marginTop: 6 };

  const submit = async () => {
    if (!name.trim()) { toast.error("Please type your full name to sign."); return; }
    if (!consent || !authorized) { toast.error("Please tick both boxes to accept."); return; }
    setBusy(true);
    try {
      await sr.publicAcceptProposal(token, { full_name: name.trim(), email: email.trim() || null, consent, authorized });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't accept the proposal"); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="accept-card" style={{ border: `2px solid ${accent}` }}>
      <h2 style={{ fontSize: 18, color: NAVY, margin: "0 0 4px", display: "flex", alignItems: "center", gap: 8 }}><PenLine size={18} style={{ color: accent }} /> Review &amp; accept</h2>
      <p style={{ fontSize: 13, color: "#64748B", marginTop: 0 }}>Type your full name to sign. Accepting starts onboarding — no payment is taken now.</p>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>Full name (your signature)
        <input style={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jordan Lee" data-testid="esign-name" />
      </label>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: "#334155", display: "block", marginTop: 12 }}>Your email
        <input style={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="esign-email" />
      </label>
      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 13, color: "#334155", marginTop: 14 }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="esign-consent" style={{ marginTop: 3 }} />
        I agree to this proposal and understand my typed name serves as my electronic signature.
      </label>
      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 13, color: "#334155", marginTop: 10 }}>
        <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} data-testid="esign-authorized" style={{ marginTop: 3 }} />
        I'm authorized to accept on behalf of {company || "my company"}.
      </label>
      <button onClick={submit} disabled={busy} data-testid="esign-accept"
        style={{ marginTop: 16, width: "100%", background: NAVY, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 15, fontWeight: 700, cursor: busy ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: busy ? 0.7 : 1 }}>
        {busy ? <Loader2 size={16} className="sr-spin" /> : <CheckCircle2 size={16} />} Accept &amp; start onboarding
      </button>
    </Card>
  );
}

function Row({ l, v }) {
  return <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}><span>{l}</span><span>{v}</span></div>;
}
